'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import {
  Sheet,
  SheetContent,
  SheetHeader,
} from '@/components/ui/sheet'
import { useSession } from 'next-auth/react'
import { getSidebarNavItems } from '@/config/navigation'
import { BrandLogo } from '@/components/brand'
import { useBranding } from '@/context/BrandingContext'
import { useActiveRole } from '@/lib/active-role'
import { RoleBadge } from './RoleBadge'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const { branding } = useBranding()
  const { data: session } = useSession()
  const { activeRole } = useActiveRole()
  const navItems = getSidebarNavItems(session?.roles, activeRole)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  const toggleSection = (label: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [label]: !prev[label]
    }))
  }

  useEffect(() => {
    const initial: Record<string, boolean> = {}
    navItems.forEach(item => {
      if (item.children) {
        initial[item.label] = false
      }
    })
    setExpandedSections(initial)
  }, [session?.roles, activeRole])

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side='left'
        className='sidebar-shell w-[280px] border-border-default bg-white p-0 md:hidden'
      >
        <SheetHeader className='flex h-16 flex-row items-center border-b border-border-default px-4'>
          <Link
            href='/'
            onClick={onClose}
            className='flex items-center gap-3'
          >
            <BrandLogo className='h-10 w-auto max-w-[9rem] object-contain' />
            <p className='text-xs font-semibold text-brand-navy'>{branding.companyName}</p>
          </Link>
        </SheetHeader>

        <div className='border-b border-border-default p-3'>
          <RoleBadge />
        </div>

        <nav className='overflow-y-auto px-3 py-4'>
          <ul className='space-y-2'>
            {navItems.map((item, idx) => {
              if (item.children) {
                const isExpanded = expandedSections[item.label]
                return (
                  <li key={idx}>
                    <button
                      onClick={() => toggleSection(item.label)}
                      className='group mb-1 flex w-full items-center justify-between rounded-md px-3 py-1.5 text-text-tertiary transition-colors hover:text-brand-navy'
                    >
                      <span className="text-[11px] font-semibold uppercase tracking-wider">{item.label}</span>
                      <Icons.chevronDown 
                        className={cn(
                          'h-3 w-3 transition-transform duration-200 opacity-60 group-hover:opacity-100',
                          isExpanded && 'rotate-180'
                        )} 
                        strokeWidth={2.5} 
                      />
                    </button>
                    {isExpanded && (
                      <div className='space-y-1 mb-3'>
                        {item.children.map((child) => (
                          <Button
                            key={child.href}
                            asChild
                            variant='ghost'
                            size='small'
                            onClick={onClose}
                            className='w-full justify-start pl-6 text-sm font-normal text-text-secondary hover:!bg-background-subtle hover:!text-brand-navy'
                          >
                            <Link href={child.href}>{child.label}</Link>
                          </Button>
                        ))}
                      </div>
                    )}
                  </li>
                )
              }

              return (
                <li key={idx}>
                  <Button
                    asChild
                    variant='ghost'
                    size='small'
                    onClick={onClose}
                    className='w-full justify-start gap-3 font-normal text-text-secondary hover:!bg-background-subtle hover:!text-brand-navy'
                  >
                    <Link href={item.href || '#'}>
                      <item.icon className='h-5 w-5 shrink-0' strokeWidth={2} />
                      <span className='text-sm'>{item.label}</span>
                    </Link>
                  </Button>
                </li>
              )
            })}
          </ul>
        </nav>
      </SheetContent>
    </Sheet>
  )
}
